import {
    Box,
    Typography,
    TextField,
    Grid,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Button,
    useTheme
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import api from '../../../../services/api';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';

export default function Vendas() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();

    const [appId, setAppId] = useState();

    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({});
    const [kpi, setKpi] = useState({});

    const [initialized, setInitialized] = useState(false);

    // filtros
    const [filters, setFilters] = useState({
        name: '',
        phone: '',
        status: '',
        start_date: '',
        end_date: '',
        ordering: '-created_at',
        page: 1
    });

    // =========================
    // APP
    // =========================
    const pegar_app_padrao = async () => {
        const res = await api.get('v1/apps/default/');
        setAppId(res.data.app.id);
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    // =========================
    // FETCH COM FILTROS
    // =========================
    const fetchOrders = async (customFilters = filters) => {
        if (!appId) return;

        try {
            const cleanFilters = Object.fromEntries(Object.entries(customFilters).filter(([_, v]) => v !== '' && v !== null));

            const query = new URLSearchParams(cleanFilters).toString();

            const res = await api.get(`v1/apps/${appId}/orders/?${query}`);

            setOrders(res.data.results);
            setPagination(res.data.pagination);
            setKpi(res.data.kpi);
        } catch (err) {
            console.error(err);
        }
    };

    // =========================
    // DEBOUNCE
    // =========================
    const debouncedFetch = useCallback(
        debounce((newFilters) => {
            fetchOrders(newFilters);
        }, 500),
        [appId]
    );

    // primeira carga
    useEffect(() => {
        if (!appId) return;

        fetchOrders(filters);
        setInitialized(true);
    }, [appId]);

    // debounce só depois de inicializar
    useEffect(() => {
        if (!appId || !initialized) return;

        debouncedFetch(filters);
    }, [filters]);

    // =========================
    // HANDLERS
    // =========================
    const updateFilter = (key, value) => {
        const updated = { ...filters, [key]: value, page: 1 };
        setFilters(updated);
    };

    const changePage = (newPage) => {
        const updated = { ...filters, page: newPage };
        setFilters(updated);
    };

    const formatCurrencyBR = (value) => {
        if (value === null || value === undefined || value === '') return '';

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(Number(value));
    };

    return (
        <Box p={3}>
            <Typography variant="h3" mb={3}>
                Vendas
            </Typography>

            {/* ================= KPI ================= */}
            <Grid container spacing={2} mb={4}>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption">Total vendido</Typography>
                            <Typography variant="h5">{formatCurrencyBR(kpi.total_vendido) || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="caption">Ticket médio</Typography>
                            <Typography variant="h5">{formatCurrencyBR(kpi.ticket_medio) || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ================= FILTROS ================= */}
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={2} mt={2.5}>
                    <TextField label="Nome" fullWidth onChange={(e) => updateFilter('name', e.target.value)} />
                </Grid>

                <Grid item xs={12} md={2} mt={2.5}>
                    <TextField label="Telefone" fullWidth onChange={(e) => updateFilter('phone', e.target.value)} />
                </Grid>

                <Grid item xs={12} md={2}>
                    <Typography>Data de início</Typography>
                    <TextField type="date" fullWidth onChange={(e) => updateFilter('start_date', e.target.value)} />
                </Grid>

                <Grid item xs={12} md={2}>
                    <Typography>Data final</Typography>
                    <TextField type="date" fullWidth onChange={(e) => updateFilter('end_date', e.target.value)} />
                </Grid>

                <Grid item xs={12} md={2} mt={2.5}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select onChange={(e) => updateFilter('status', e.target.value)}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="draft">Rascunho</MenuItem>
                            <MenuItem value="pending">Pendente</MenuItem>
                            <MenuItem value="approved">Aprovado</MenuItem>
                            <MenuItem value="paid">Pago</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={2} mt={2.5}>
                    <FormControl fullWidth>
                        <InputLabel>Ordenar</InputLabel>
                        <Select onChange={(e) => updateFilter('ordering', e.target.value)}>
                            <MenuItem value="-created_at">Mais recentes</MenuItem>
                            <MenuItem value="created_at">Mais antigos</MenuItem>
                            <MenuItem value="-total_value">Maior valor</MenuItem>
                            <MenuItem value="total_value">Menor valor</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* ================= LISTA ================= */}
            {orders.map((order) => (
                <Card key={order.id} sx={{ mb: 2, cursor: 'pointer' }} onClick={() => history.push(`/dashboard/detailsorder/${order.id}/`)}>
                    <CardContent>
                        <Grid container>
                            <Grid item xs={3}>
                                <Typography>
                                    {order.user_detail?.first_name} {order.user_detail?.last_name}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography>R$ {order.total_value}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography>{order.items_detail?.length} produtos</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                {order.status === 'draft' && <Chip label="Rascunho" size="small" />}
                                {order.status === 'pending' && (
                                    <Chip
                                        sx={{ background: '#D0D622', color: '#708076', textTransform: 'none', fontWeight: 600 }}
                                        label="Pendente"
                                        size="small"
                                    />
                                )}
                                {order.status === 'approved' && (
                                    <Chip
                                        sx={{ background: '#06731B', color: '#D4D7D9', textTransform: 'none', fontWeight: 600 }}
                                        label="Aprovado"
                                        size="small"
                                    />
                                )}
                                {order.status === 'paid' && (
                                    <Chip
                                        sx={{ background: '#27F550', color: '#708076', textTransform: 'none', fontWeight: 600 }}
                                        label="Pago"
                                        size="small"
                                    />
                                )}
                                {order.status === 'canceled' && (
                                    <Chip
                                        sx={{ background: '#D64922', color: '#D4D7D9', textTransform: 'none', fontWeight: 600 }}
                                        label="Cancelado"
                                        size="small"
                                    />
                                )}
                                {order.status === 'refunded' && (
                                    <Chip
                                        sx={{ background: '#2192DE', color: '#D4D7D9', textTransform: 'none', fontWeight: 600 }}
                                        label="Reembolsado"
                                        size="small"
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ))}

            {/* ================= PAGINAÇÃO ================= */}
            <Box mt={3} display="flex" gap={2}>
                <Button disabled={filters.page <= 1} onClick={() => changePage(filters.page - 1)}>
                    Anterior
                </Button>

                <Typography>
                    Página {pagination.page} de {pagination.total_pages}
                </Typography>

                <Button disabled={filters.page >= pagination.total_pages} onClick={() => changePage(filters.page + 1)}>
                    Próxima
                </Button>
            </Box>
        </Box>
    );
}
